"use client";

import { getSession, signIn, useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiGithub } from "react-icons/fi";
import { useRouter, useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { LoginSchema } from "@/app/schemas";
import { login } from "@/app/actions/login";
import { ButtonS } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormError } from "@/app/components/FormError";
import { FormSuccess } from "@/app/components/FormSucess";
import Heading from "../components/Heading";
import Container from "../components/Container";
import Button from "../components/Button";

interface LoginSelectProps {
    currentUser?: any;
}

const LoginClient: React.FC<LoginSelectProps> = () => {
    const router = useRouter();
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{ error?: string; success?: string }>(
        {},
    );
    const searchParams = useSearchParams();
    const urlError =
        searchParams?.get("error") === "OAuthAccountNotLinked"
            ? "Email already in use with a different provider!"
            : "";

    const { data: session, status } = useSession();

    const form = useForm<z.infer<typeof LoginSchema>>({
        resolver: zodResolver(LoginSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    // Handler para OAuth providers (Google y GitHub)
    const handleSocialLogin = async (provider: "google" | "github") => {
        // const userAgent = navigator.userAgent;
        // if (userAgent.includes("Instagram")) {
        //     setShowInstagramWarning(true);
        //     return;
        // }

        if (isSubmitting) return;

        try {
            // Redirigir a la raíz después del login OAuth exitoso
            await signIn(provider, {
                callbackUrl: "/"
            });
        } catch (error) {
            console.error(`${provider} login error:`, error);
            toast.error(`Something went wrong with ${provider} login`);
        }
    };

    const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setMessage({});

        try {
            const data = await login(values);

            if (data.error) {
                setMessage({ error: data.error });
                return;
            }

            if (data.success === "Confirmation email sent!") {
                form.reset();
                setMessage({ success: data.success });
                return;
            }

            const result = await signIn("credentials", {
                ...values,
                redirect: false,
            });

            if (result?.ok && !result?.error) {
                toast.success("Logged in!");

                const updatedSession = await getSession();

                if (updatedSession?.user?.id) {
                    router.replace(`/dashboard/${updatedSession.user.id}`);
                } else {
                    router.replace("/");
                }
            } else {
                setMessage({ error: "Invalid credentials" });
            }

        } catch (error) {
            setMessage({ error: "Something went wrong" });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (urlError) {
            toast.error(urlError);
        }
    }, [urlError]);

    return (
        <div className="flex flex-col gap-2 text-black py-8">
            <Container>
                <Heading title="Welcome back" subtitle="Login to your account!" />
                <div className="text-neutral-500 text-center my-4 text-sm gap-4">
                    <Button
                        outline
                        label="Continue with Google"
                        icon={FcGoogle}
                        onClick={() => handleSocialLogin("google")}
                    />
                    <div className="mt-2"></div>
                    <Button
                        outline
                        label="Continue with Github"
                        icon={FiGithub}
                        onClick={() => handleSocialLogin("github")}
                    />
                </div>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <div className="space-y-4 mt-2">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-900">Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                disabled={isSubmitting}
                                                placeholder="example@example.com"
                                                type="email"
                                                className="bg-white text-black border-gray-400 placeholder-gray-500"
                                            />
                                        </FormControl>
                                        <FormMessage className="text-red-400 text-xs" />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-900">Password</FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <Input
                                                    {...field}
                                                    disabled={isSubmitting}
                                                    placeholder="******"
                                                    type={showPassword ? "text" : "password"}
                                                    className="bg-white border-gray-400 text-black placeholder-gray-500 pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                                                    disabled={isSubmitting}
                                                >
                                                    {showPassword ? (
                                                        <FaEyeSlash size={16} />
                                                    ) : (
                                                        <FaEye size={16} />
                                                    )}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage className="text-red-500 text-xs" />
                                    </FormItem>
                                )}
                            />

                        </div>

                        <FormError message={message.error || urlError} />
                        <FormSuccess message={message.success} />

                        <ButtonS
                            type="submit"
                            disabled={isSubmitting}
                            className="h-10 w-full rounded-lg bg-emerald-600 text-white font-semibold text-md py-3 hover:bg-emerald-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? "Logging in..." : "Login"}
                        </ButtonS>
                    </form>
                </Form>

                <div className="flex justify-end mt-4 font-bold">
                    <span
                        onClick={() => router.push("/auth/forgotpassword")}
                        className="text-emerald-600 cursor-pointer hover:underline text-sm"
                    >
                        Forgot password?
                    </span>
                </div>

                <hr className="border-gray-400 my-4" />

                <div className="text-neutral-500 text-center my-4 text-sm">
                    <p>
                        First time here?
                        <span
                            onClick={() => router.push("/register")}
                            className="text-emerald-600 cursor-pointer hover:underline"
                        >
                            {" "}
                            Register
                        </span>
                    </p>
                </div>
            </Container>
        </div>
    );
};

export default LoginClient;